import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getInvitationService,
  type Invitation,
  type InvitationType,
  type NewInvitation,
} from '@arcual/lib-app-platform';
import { handleHttpError } from '@/utils/errorHandlers';
import { CodeGenerator } from '@/utils/codeGeneratorUtils';
import { InvitationCodeExporter } from '@/utils/exporter/invitation/InvitationCodeExporter';

const InvitationStoreName = 'InvitationStore';

export const useInvitationStore = defineStore(InvitationStoreName, () => {
  const invitations = ref<Invitation[]>([]);
  const invitation = ref<Invitation | null>(null);

  const codeGenerator = new CodeGenerator();
  const invitationCodeExporter = new InvitationCodeExporter();

  const getInvitations = async (): Promise<Invitation[]> => {
    try {
      invitations.value = await getInvitationService().getInvitations();
      return invitations.value;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const getInvitationById = async (id: string): Promise<Invitation> => {
    try {
      invitation.value = await getInvitationService().getInvitationById(id);
      return invitation.value;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const sendInvitation = async (invitationData: NewInvitation): Promise<Invitation> => {
    try {
      const response = await getInvitationService().sendInvitation(invitationData);
      invitations.value.push(response);
      return response;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const acceptInvitation = async (id: string): Promise<Invitation> => {
    try {
      return await getInvitationService().acceptInvitation(id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const rejectInvitation = async (id: string): Promise<Invitation> => {
    try {
      return await getInvitationService().rejectInvitation(id);
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  const createInvitationCodes = async (amount: number, entityId: string): Promise<Invitation[]> => {
    const codes = codeGenerator.generateBatch(amount);

    return getInvitationService()
      .createInvitationCodes({ codes, entityId })
      .then((response) => {
        invitationCodeExporter.exportCodes(codes, entityId);
        return response;
      })
      .catch((error) => {
        handleHttpError(error);
        return Promise.reject(error);
      });
  };

  const updateInvitationByCode = async (code: string): Promise<Invitation> => {
    return getInvitationService()
      .updateInvitationByCode(code)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        handleHttpError(error);
        return Promise.reject(error);
      });
  };

  const sendEmailInvitations = async (
    type: InvitationType,
    entityId: string,
    emails: string[],
  ): Promise<Invitation[]> => {
    try {
      const response = await getInvitationService().sendEmailInvitations({ type, entityId, emails });
      return response;
    } catch (error) {
      handleHttpError(error);
      return Promise.reject(error);
    }
  };

  /**
   * Reset the store to its initial state
   */
  const reset = () => {
    invitations.value = [];
    invitation.value = null;
  };

  return {
    invitations,
    invitation,
    getInvitations,
    getInvitationById,
    sendInvitation,
    acceptInvitation,
    rejectInvitation,
    createInvitationCodes,
    updateInvitationByCode,
    sendEmailInvitations,
    reset,
  };
});
